// DS8.2.3
import React, { useEffect, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  AddToCollectionDoneBiEvent,
  AddToPrivateCollectionDoneBiEvent,
  BiContextProvidedValue,
  BiData,
  BuyCoreButtonBiEvent,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import DisclosureBanner from '@wix/da-ds/pkg/DisclosureBanner';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import CollectionsList from '../../../CollectionPanel/CollectionList';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DSModal, {
  MODAL_FOOTER_BUTTON_SIZE,
} from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import DSPopover, {
  POPOVER_FOOTER_BUTTON_SIZE,
} from '@wix/da-ds/pkg/popovers/dsPresets/Popover';
import NewCollectionPopup from './NewCollectionPopup';
import { getDeviationResourceType } from '../../../DeviationViews/_helpers';
import { getCurrentUserHasCore } from '../../../publicSession/selectors';
import BuyCoreBrandedButton from '../../../Button/core/dsPresets/BuyCoreBrandedButton/BuyCoreBrandedButton';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import s from './AddToCollectionPopup.scss';

export interface Props {
  collectibleDeviation: PapiDeviation;
  className?: string;
  isPrivateCollections: boolean;
  totalCountOfPrivatelyCollectedDeviations?: number;
  isInitialized: boolean;
  hasMoreCollections: boolean;
  collectionsCount: number;
  ignorePreselectedCollections?: boolean;
  notificationQueue?: string;
  selectedFolderIds: number[];
  initializeCollectionsForDeviation: (
    deviationId: number,
    isPrivateCollections: boolean,
    faveCount: number,
    ignorePreselectedCollections?: boolean
  ) => void;
  commitCollections: (
    deviation: PapiDeviation,
    notificationQueue: string,
    isPrivateCollections: boolean
  ) => void;
  removeQueues: (predicate: (queueName: string) => boolean) => void;
  onClose: () => void;
}

const MAX_ALLOWED_PRIVATE_DEVIATIONS_FOR_NON_CORE = 10;

const AddToCollectionPopup: React.FC<Props> = ({
  isPrivateCollections,
  totalCountOfPrivatelyCollectedDeviations = 0,
  collectibleDeviation,
  collectibleDeviation: { deviationId },
  className,
  isInitialized,
  hasMoreCollections,
  collectionsCount,
  ignorePreselectedCollections,
  notificationQueue,
  selectedFolderIds,
  initializeCollectionsForDeviation,
  commitCollections,
  removeQueues,
  onClose,
}) => {
  const { t } = useTranslation();
  const hasCoreMembership = useSelector(getCurrentUserHasCore);

  const reachedCollectLimit =
    isPrivateCollections &&
    !hasCoreMembership &&
    totalCountOfPrivatelyCollectedDeviations >=
      MAX_ALLOWED_PRIVATE_DEVIATIONS_FOR_NON_CORE;

  useEffect(() => {
    if (!isInitialized) {
      initializeCollectionsForDeviation(
        deviationId,
        isPrivateCollections,
        get(collectibleDeviation, 'stats.favourites', 0),
        ignorePreselectedCollections
      );
    }
  }, [isInitialized, initializeCollectionsForDeviation, deviationId]);

  // flush everything from a given (via props) notificationQueue on init
  // so that on save the new notification doesn't overlap with any old ones
  useEffect(() => {
    removeQueues((name: string) => name === notificationQueue);
  }, [notificationQueue, removeQueues]);

  const isMobile = useContext(MobileContext);
  const [filterValue, setFilterValue] = useState<string>('');
  const [showingNewCollectionPopup, setShowNewCollectionPopup] =
    useState(false);

  const onFilterInputChange = e => setFilterValue(e.target.value);

  const onDone = () => {
    yieldToMain(() => {
      commitCollections(
        collectibleDeviation,
        notificationQueue || 'default',
        isPrivateCollections
      );
      onClose();
    });
  };

  if (showingNewCollectionPopup) {
    return (
      <NewCollectionPopup
        className={className}
        collectibleDeviation={collectibleDeviation}
        isPrivateCollections={isPrivateCollections}
        onClose={() => setShowNewCollectionPopup(false)}
      />
    );
  }
  const { author } = collectibleDeviation;
  const filterInput =
    !hasMoreCollections && collectionsCount >= 5 ? (
      <TextInput
        id="search-input"
        className={s['filter-input']}
        biData={BiData<DefaultBiEvent>({
          evid: 2,
          click_info: 'addToCollectionPopover.filterInput',
          sectionname: BiContextProvidedValue,
          widgetname: BiContextProvidedValue,
          typeid: BiContextProvidedValue,
          itemid: BiContextProvidedValue,
        })}
        label={t('addToCollectionPopover.input.filter.placeholder')}
        placeholder={t('addToCollectionPopover.input.filter.placeholder')}
        value={filterValue}
        onChange={onFilterInputChange}
        prefix={<SearchIcon disableDefaultColors />}
        noBottomSpacing
        isLabelHidden
      />
    ) : undefined;
  const footerPrimaryButton = reachedCollectLimit ? (
    <BuyCoreBrandedButton
      biData={
        isPrivateCollections
          ? ({
              referral_info: ReferralType.PRIVATE_COLLECTIONS_POPUP,
            } as BuyCoreButtonBiEvent)
          : undefined
      }
      size="medium"
      withSaleSparkles
    >
      {t('common.upgradeCoreCTA')}
    </BuyCoreBrandedButton>
  ) : (
    <SolidButton
      variant="approval-green"
      biData={BiData<
        AddToPrivateCollectionDoneBiEvent | AddToCollectionDoneBiEvent
      >({
        evid: isPrivateCollections ? 335 : 331,
        creatorid: (author && author.useridUuid) ?? '',
        typeid: getDeviationResourceType(collectibleDeviation),
        itemid: deviationId,
        sectionname: undefined, // sectionname only applies to green site for this evid
        collectionid:
          selectedFolderIds.length > 1 ? undefined : selectedFolderIds[0],
        num_collections: selectedFolderIds.length,
      } as any)}
      onClick={onDone}
      size={isMobile ? MODAL_FOOTER_BUTTON_SIZE : POPOVER_FOOTER_BUTTON_SIZE}
    >
      {t('common.done')}
    </SolidButton>
  );
  const newCollectionButton = (
    <TextButton
      onClick={() => setShowNewCollectionPopup(true)}
      icon={AddIcon}
      size="small"
      variant="main"
      disabled={isPrivateCollections && !hasCoreMembership}
      biData={BiData<DefaultBiEvent>({
        evid: 2,
        click_info: isPrivateCollections
          ? 'addToCollectionPopover.newPrivateCollection'
          : 'addToCollectionPopover.newCollection',
        sectionname: BiContextProvidedValue,
        widgetname: BiContextProvidedValue,
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
      })}
    >
      {t('addToCollectionPopover.button.newCollection')}
    </TextButton>
  );

  const popupTitle = t(
    isPrivateCollections
      ? 'addToCollectionPopover.private.title'
      : 'addToCollectionPopover.title'
  );

  const popupSubtitle = isPrivateCollections
    ? t('addToCollectionPopover.private.subtitle')
    : undefined;

  const popupContent = (
    <>
      {!isPrivateCollections && filterInput}
      {isPrivateCollections && !hasCoreMembership && (
        <DisclosureBanner
          className={s['core-banner']}
          message={t(
            reachedCollectLimit
              ? 'addToCollectionPopover.coreBanner.reachedLimit.message'
              : 'addToCollectionPopover.coreBanner.message'
          )}
          ctaLabel={t('addToCollectionPopover.coreBanner.ctaLabel')}
          ctaBiData={BiData<BuyCoreButtonBiEvent>({
            evid: 375,
            referral_info: ReferralType.PRIVATE_COLLECTIONS_POPUP,
          })}
        />
      )}
      <CollectionsList
        collectibleDeviation={collectibleDeviation}
        isPrivateCollections={isPrivateCollections}
        filterValue={filterValue}
        allDisabled={reachedCollectLimit}
      />
    </>
  );

  if (isMobile) {
    return (
      <DSModal
        isOpen
        heading={popupTitle}
        subheading={popupSubtitle}
        className={className}
        contentClassName={s['content-wrapper']}
        showHeaderBorder
        showFooterBorder
        footerRight={[footerPrimaryButton]}
        footerLeft={[newCollectionButton]}
        withScrollableContentWrapper={false}
        onRequestClose={onClose}
      >
        {popupContent}
      </DSModal>
    );
  }

  return (
    <DSPopover
      heading={popupTitle}
      subheading={popupSubtitle}
      className={c(s['popover-root'], className)}
      contentClassName={s['content-wrapper']}
      showFooterBorder
      footerRight={footerPrimaryButton}
      footerLeft={newCollectionButton}
      onCloseClick={onClose}
      content={popupContent}
    />
  );
};
AddToCollectionPopup.displayName = 'AddToCollectionPopup';

export default AddToCollectionPopup;
